import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { Modal } from '@/deploy/components/ui/Modal';
import { ApiError } from '@/deploy/services/api';
import { apiServicesService, type ApiInstanceCandidate, type ApiService } from '@/deploy/services/api-services.service';
import type { ApiEnvId } from '@/deploy/services/api-pterodactyl-config.service';
import { apiGroupLabel, canonicalApiEnv } from '@/deploy/utils/apiEnvs';
import { findInstance, instanceDisplayName, instanceErrorMessage } from '@/deploy/utils/apiInstances';
import pageStyles from '@/deploy/pages/ApiServices.module.scss';

// ============================================================
// Rattacher un serveur Ptero existant à une instance sur l'env courant : serveurs de l'image
// que ni une autre instance ni une autre API du projet n'ont déjà.
// ============================================================

export function ApiAttachServerModal({ projectId, api, instanceKey, envId, onClose, onChanged }: {
  projectId: string;
  api: ApiService;
  instanceKey: string;
  envId: ApiEnvId;
  onClose: () => void;
  onChanged: () => Promise<void> | void;
}) {
  const env = canonicalApiEnv(envId);
  const envLabel = apiGroupLabel(env);
  const inst = findInstance(api, instanceKey);
  const name = inst ? instanceDisplayName(api, inst) : api.name;
  const [candidates, setCandidates] = useState<ApiInstanceCandidate[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCandidates(await apiServicesService.instanceCandidates(projectId, api.id, instanceKey, env));
    } catch (e) {
      setError(instanceErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [projectId, api.id, instanceKey, env]);

  useEffect(() => { void load(); }, [load]);

  const attach = async (c: ApiInstanceCandidate) => {
    // Candidat déjà signalé d'une autre image : confirmation avant l'envoi, puis rattachement forcé
    if (!c.imageMatches
      && !confirm(`Le serveur ${c.name} n'a pas l'image de cette API (template ${api.dockerImage}). Le rattacher quand même ?`)) return;
    setBusy(c.id);
    setError(null);
    const call = (force: boolean) => apiServicesService.attachInstance(projectId, api.id, instanceKey, {
      environment: env, serverId: c.id, force: force || undefined,
    });
    try {
      try {
        await call(!c.imageMatches);
      } catch (e) {
        // Image différente du template : confirmation explicite, puis rattachement forcé
        const mismatch = e instanceof ApiError && e.status === 409
          && (e.body as { imageMismatch?: boolean } | undefined)?.imageMismatch;
        if (!mismatch || !confirm(`${e instanceof Error ? e.message : ''}\n\nRattacher quand même ?`)) throw e;
        await call(true);
      }
      await onChanged();
      onClose();
    } catch (e) {
      setError(instanceErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title={`Rattacher un serveur à ${name}`}
      subtitle={`Serveurs Ptero de ${envLabel} avec l'image de ${api.name}`}
      width="md"
      footer={<Button variant="ghost" onClick={onClose} disabled={busy !== null}>Fermer</Button>}
    >
      <div className={pageStyles.form}>
        {error && <div className={pageStyles.error}>{error}</div>}
        {loading && <div className={pageStyles.fieldHint}>Lecture des serveurs Ptero…</div>}
        {!loading && candidates && candidates.length === 0 && (
          <div className={pageStyles.hintBox}>
            <Icon name="warn" size={12} /> Aucun serveur libre avec l'image de cette API sur {envLabel}.
          </div>
        )}
        {candidates && candidates.length > 0 && (
          <div className={pageStyles.results}>
            {candidates.map((c) => (
              <div key={c.id} className={pageStyles.resultRow} style={{ border: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
                <Icon name="server" size={12} />
                <strong style={{ fontFamily: 'var(--font-family-mono)', fontSize: 12 }}>{c.name}</strong>
                {c.version && <Badge tone="info">v{c.version}</Badge>}
                {c.endpoint && <code style={{ fontSize: 11 }}>{c.endpoint}</code>}
                {c.retired && <Badge tone="muted">retiré d'une instance</Badge>}
                {!c.imageMatches && <Badge tone="warning">autre image</Badge>}
                <span style={{ flex: 1 }} />
                <Button variant="primary" size="sm" loading={busy === c.id} disabled={busy !== null}
                  onClick={() => void attach(c)}>
                  Rattacher
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className={pageStyles.fieldHint}>
          Le serveur rattaché devient celui de l'instance pour Deploy, Release, power, console et fichiers.
          Il reste à répartir l'instance pour que des serveurs de jeu la reçoivent.
        </div>
      </div>
    </Modal>
  );
}
